/**
 * 4M なぜなぜ分析デモ (/why-demo)
 *
 * 1つの事象 (5〜200字) を入力 → Gemini 3.1 Flash Lite が 4M (人/機械/材料/方法) の
 * 各観点で 5段なぜなぜ + 根本原因 + 即時/恒久対策を生成. タブで切り替えて閲覧.
 *
 * 体験版なので organizationId='demo' で叩き、登録不要・保存なし・キャッシュなし.
 * 結果末尾に WhyTrace+ (whytrace.com) への送客 CTA を配置.
 *
 * 仕様書: docs/HANDOVER_why-analysis-demo-from-anzenai.md
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  RefreshCw,
  AlertTriangle,
  ArrowUpRight,
  Lightbulb,
  Loader2,
  QrCode,
  FileText,
  ChevronRight,
  Save,
  ArrowLeft,
  Printer,
} from 'lucide-react';
import {
  analyzeWhyChain,
  pickRandomSamples,
  fourMLabel,
  FOUR_M_KEYS,
  type FourMKey,
  type WhyAnalysisResult,
  type Analysis,
} from '../services/whyAnalysisService';
import { WHY_ANALYSIS_MARKER } from '../utils/caseClassification';

const trackEvent = (eventName: string, params: Record<string, unknown> = {}) => {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { gtag?: (cmd: string, action: string, p: Record<string, unknown>) => void };
  if (typeof w.gtag === 'function') {
    w.gtag('event', eventName, params);
  }
};

// ---------------- Sub-components ---------------- //

/** 観点見出し: 「○ の観点でのなぜなぜ分析」+ 説明 */
const ViewHeader: React.FC<{ viewKey: FourMKey }> = ({ viewKey }) => (
  <div className="mb-5">
    <h3 className="text-lg md:text-xl font-bold text-industrial-900 mb-1.5">
      {fourMLabel(viewKey)} の観点でのなぜなぜ分析
    </h3>
    <p className="text-sm text-slate-500">
      この観点に着目して原因を 5段階で深掘りした結果です。
    </p>
  </div>
);

/**
 * なぜなぜ 5段ツリー + 根本原因.
 * AnzenAI のデザインに準拠:
 *  - 縦線 + 丸ドット (amber)
 *  - 各「なぜ」は白カードに amber バッジ風 question + answer 本文
 *  - 根本原因は emerald 系の太枠で視覚的に区別
 */
const WhyTree: React.FC<{ analysis: Analysis; viewKey: FourMKey }> = ({ analysis, viewKey }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-7 shadow-sm">
    <ViewHeader viewKey={viewKey} />

    <ol className="relative ml-2 pl-6 space-y-4">
      {/* 縦線: タイムラインを連続的に見せる (上から下まで). amber-200 */}
      <div className="absolute left-[7px] top-3 bottom-[60px] w-0.5 bg-amber-200" aria-hidden />

      {analysis.whys.map((w, idx) => (
        <li key={idx} className="relative">
          <span
            className="absolute -left-[28px] top-3 w-4 h-4 rounded-full bg-amber-400 ring-4 ring-white shadow-sm"
            aria-hidden
          />
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="inline-block bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded-md mb-2">
              なぜ {idx + 1}: {w.question || `なぜ${idx + 1}?`}
            </div>
            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{w.answer}</p>
          </div>
        </li>
      ))}

      {/* 推定される根本原因 (緑で強調) */}
      <li className="relative pt-2">
        <span
          className="absolute -left-[28px] top-[18px] w-4 h-4 rounded-full bg-emerald-500 ring-4 ring-white shadow-sm"
          aria-hidden
        />
        <div className="bg-white border-2 border-emerald-500 rounded-xl p-4 shadow-sm">
          <div className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-1 rounded-md mb-2">
            推定される根本原因
          </div>
          <p className="text-sm font-bold text-industrial-900 leading-relaxed whitespace-pre-line">
            {analysis.rootCause || '(該当なし)'}
          </p>
        </div>
      </li>
    </ol>
  </div>
);

const Countermeasures: React.FC<{ analysis: Analysis }> = ({ analysis }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-7 shadow-sm">
    <h3 className="text-lg md:text-xl font-bold text-industrial-900 mb-5">この観点での対策案</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* 即時対策 — amber */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="bg-amber-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-md">
            即時対策
          </span>
          <span className="text-xs text-amber-900/80">今日からできる応急策</span>
        </div>
        <h4 className="text-base font-bold text-industrial-900 mb-1.5">
          {analysis.immediate.title || '(未設定)'}
        </h4>
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
          {analysis.immediate.description}
        </p>
      </div>

      {/* 恒久対策 — emerald */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="bg-emerald-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-md">
            恒久対策
          </span>
          <span className="text-xs text-emerald-900/80">再発防止の仕組み</span>
        </div>
        <h4 className="text-base font-bold text-industrial-900 mb-1.5">
          {analysis.permanent.title || '(未設定)'}
        </h4>
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
          {analysis.permanent.description}
        </p>
      </div>
    </div>
  </div>
);

const Disclaimer: React.FC = () => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 leading-relaxed flex items-start gap-2">
    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
    <p>
      この結果は AI による <strong>簡易分析の補助</strong> です。実際の原因究明・対策決定は、現場の事実確認と関係者の議論のうえで行ってください。
    </p>
  </div>
);

/**
 * 安全ポスト＋本体への送客 CTA. 「現場でヒヤリを継続的に集めるなら本体登録」.
 * WhyTrace+ への送客と棲み分け: こちらは「上流 = 入口」、WhyTrace+ は「下流 = 深掘り管理」.
 */
const AnzenPostCta: React.FC<{ onSignup: () => void; onDownloadDeck: () => void }> = ({ onSignup, onDownloadDeck }) => {
  const onSignupClick = () => {
    trackEvent('why_demo_to_anzenpost_signup_click', { from: 'why_demo_result' });
    onSignup();
  };
  const onDeckClick = () => {
    trackEvent('why_demo_to_anzenpost_deck_click', { from: 'why_demo_result' });
    onDownloadDeck();
  };
  return (
    <div className="bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-2xl p-6 md:p-8 shadow-lg">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest opacity-80 mb-1">For ongoing collection</p>
          <h3 className="text-xl md:text-2xl font-bold mb-2 flex items-center gap-2">
            <QrCode size={22} />
            こうした分析を「現場で起きるたびに自動で」やりたい?
          </h3>
          <p className="text-sm text-orange-50 leading-relaxed mb-4">
            安全ポスト＋なら、現場に QR を貼って 30秒で報告 → AI が 4M 分析 → 改善追跡まで一気通貫。
            外国人作業員の多言語ヒヤリ報告にも対応。<strong>無料プランから始められます</strong>。
          </p>
        </div>
        <div className="flex-shrink-0 flex flex-col gap-2">
          <button
            onClick={onSignupClick}
            className="inline-flex items-center justify-center gap-2 bg-white text-orange-700 font-bold text-sm px-5 py-3 rounded-xl whitespace-nowrap hover:bg-orange-50 transition"
          >
            無料で始める
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={onDeckClick}
            className="inline-flex items-center justify-center gap-2 bg-orange-700/40 text-white font-bold text-xs px-5 py-2.5 rounded-xl whitespace-nowrap hover:bg-orange-700/60 transition border border-white/30"
          >
            <FileText className="h-3.5 w-3.5" />
            概要資料 PDF を見る
          </button>
        </div>
      </div>
    </div>
  );
};

const WhyTraceCta: React.FC = () => {
  const onClick = () => trackEvent('whytrace_handoff_click', { from: 'why_demo' });
  return (
    <a
      href="https://whytrace.com"
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="block bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-2xl p-6 md:p-8 hover:from-indigo-700 hover:to-purple-800 transition shadow-lg"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest opacity-80 mb-1">For deeper analysis</p>
          <h3 className="text-xl md:text-2xl font-bold mb-2">
            さらに深く・複数事象を整理するなら WhyTrace+
          </h3>
          <p className="text-sm text-indigo-100 leading-relaxed">
            観点の追加・多段の深掘り・対策の進捗管理・組織ナレッジ化は姉妹サービス WhyTrace+ で。
            複数事象を横断して根本原因を抽出、対策の割当・期限・進捗まで一気通貫で管理できます。
          </p>
        </div>
        <div className="flex-shrink-0 inline-flex items-center gap-2 bg-white text-indigo-700 font-bold text-sm px-5 py-3 rounded-xl whitespace-nowrap">
          WhyTrace+ を見る
          <ArrowUpRight className="h-4 w-4" />
        </div>
      </div>
    </a>
  );
};

// ---------------- Main component ---------------- //

interface WhyAnalysisDemoProps {
  /** 既存ケース詳細からプリセット入力するときに渡す */
  initialProblem?: string;
  /** プリセット渡されたときに即時実行するなら true. デフォルト false */
  autoStart?: boolean;
  /** ケース詳細から呼ばれたとき、戻る先のラベル. 例: 'ケース詳細に戻る' */
  returnLabel?: string;
  /** 「戻る」ボタンを押したときの動作. 渡されない場合は表示しない */
  onReturn?: () => void;
  /** 「この分析結果をケースに保存」を押したときの動作 (本登録ユーザ・ケース詳細から呼ばれた時) */
  onSaveToCase?: (lessonText: string) => void;
  /** 安全ポスト＋ への登録誘導 (Free プラン). 結果ページの CTA で呼ぶ */
  onSignupToAnzenPost?: () => void;
  /** 概要資料 PDF を見る (新タブ開く挙動はここで決める) */
  onShowAnzenPostDeck?: () => void;
}

type Stage = 'input' | 'loading' | 'result' | 'error';

export const WhyAnalysisDemo: React.FC<WhyAnalysisDemoProps> = ({
  initialProblem = '',
  autoStart = false,
  returnLabel,
  onReturn,
  onSaveToCase,
  onSignupToAnzenPost,
  onShowAnzenPostDeck,
}) => {
  const [stage, setStage] = useState<Stage>('input');
  const [problem, setProblem] = useState<string>(initialProblem);
  const [result, setResult] = useState<WhyAnalysisResult | null>(null);
  const [active, setActive] = useState<FourMKey>('man');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [samples, setSamples] = useState<string[]>([]);
  const busy = useRef(false);

  useEffect(() => {
    setSamples(pickRandomSamples(4));
  }, []);

  const reshuffleSamples = () => setSamples(pickRandomSamples(4));

  const runAnalysis = async (text: string) => {
    if (busy.current) return;
    const t = text.trim();
    if (t.length < 5) {
      setErrorMsg('事象は5文字以上で入力してください');
      return;
    }
    if (t.length > 200) {
      setErrorMsg('事象は200文字以内で入力してください');
      return;
    }
    setErrorMsg('');
    busy.current = true;
    setStage('loading');
    trackEvent('why_analysis_start', { problem_length: t.length });
    try {
      const r = await analyzeWhyChain(t, 'demo');
      setResult(r);
      setActive('man');
      setStage('result');
      trackEvent('why_analysis_completed', { problem_length: t.length });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'なぜなぜ分析に失敗しました';
      setErrorMsg(msg);
      setStage('error');
    } finally {
      busy.current = false;
    }
  };

  // 既存ケースからプリセット渡されたら autoStart で即実行
  useEffect(() => {
    if (autoStart && initialProblem.trim().length >= 5 && stage === 'input') {
      runAnalysis(initialProblem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, initialProblem]);

  const resetToInput = () => {
    setStage('input');
    setResult(null);
    setProblem('');
    setErrorMsg('');
    reshuffleSamples();
  };

  const remaining = useMemo(() => Math.max(0, 200 - problem.length), [problem]);

  // 結果からケースに保存するテキストを構築 (lesson field に追記する用)
  const buildLessonText = (): string => {
    if (!result) return '';
    const ts = new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
    // マーカーは caseClassification.WHY_ANALYSIS_MARKER と一致させること (一覧バッジ/詳細分離の判定に使う)
    const sectionTitle = `\n\n${WHY_ANALYSIS_MARKER} (${ts}) ━━━`;
    const sections = FOUR_M_KEYS.map((key) => {
      const a = result.analyses[key];
      const whys = a.whys.map((w, i) => `  ${i + 1}. Q: ${w.question}\n     A: ${w.answer}`).join('\n');
      return `\n【${fourMLabel(key)}】\n${whys}\n  ▶ 根本原因: ${a.rootCause}\n  ▶ 即時対策: ${a.immediate.title} — ${a.immediate.description}\n  ▶ 恒久対策: ${a.permanent.title} — ${a.permanent.description}`;
    }).join('\n');
    return `${sectionTitle}${sections}`;
  };

  return (
    <div className="py-12 md:py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* 戻る (ケース詳細から来た場合のみ) */}
      {onReturn && (
        <button
          onClick={onReturn}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4 font-medium no-print"
        >
          <ArrowLeft size={16} />
          {returnLabel || '戻る'}
        </button>
      )}

      {/* Header */}
      <div className="text-center mb-10 no-print">
        <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1 rounded-full mb-3">
          <Search className="h-3.5 w-3.5" />
          無料デモ・登録不要
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-industrial-900 mb-3 leading-tight">
          4M なぜなぜ分析 デモ
        </h1>
        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
          事象を1つ入力すると、AI が <strong>人 / 機械 / 材料 / 方法</strong> の 4観点で<br className="hidden md:block" />
          5段なぜなぜを別々に深掘りし、即時・恒久の対策案を提示します。
        </p>
      </div>

      {/* ---------- Stage: input ---------- */}
      {stage === 'input' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
            <label className="block text-sm font-bold text-industrial-900 mb-2">
              事象（5〜200文字）
            </label>
            <textarea
              value={problem}
              onChange={(e) => setProblem(e.target.value.slice(0, 200))}
              rows={3}
              placeholder="例: 作業員が足場の開口部から墜落し負傷した"
              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-slate-400">{problem.length} / 200 (残り {remaining})</p>
              {errorMsg && (
                <p className="text-xs text-red-600 font-medium">{errorMsg}</p>
              )}
            </div>

            <button
              onClick={() => runAnalysis(problem)}
              disabled={problem.trim().length < 5}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold py-3.5 rounded-xl shadow-md shadow-blue-500/30 hover:from-blue-700 hover:to-indigo-800 hover:-translate-y-0.5 hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              <Search className="h-5 w-5" />
              4M で深掘り分析する
            </button>
          </div>

          {/* Samples */}
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                サンプル事例から試す
              </p>
              <button
                onClick={reshuffleSamples}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                他の例
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {samples.map((s, i) => (
                <button
                  key={`${s}-${i}`}
                  onClick={() => {
                    setProblem(s);
                    setErrorMsg('');
                  }}
                  className="text-left bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 rounded-lg px-4 py-3 text-xs text-slate-700 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---------- Stage: loading ---------- */}
      {stage === 'loading' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 md:p-14 text-center shadow-sm">
          <Loader2 className="h-10 w-10 text-blue-600 mx-auto mb-5 animate-spin" />
          <h3 className="text-lg font-bold text-industrial-900 mb-1">4観点で分析中…</h3>
          <p className="text-sm text-slate-500">人・機械・材料・方法の各視点で別々に深掘りします (5〜10秒)</p>
        </div>
      )}

      {/* ---------- Stage: error ---------- */}
      {stage === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <p className="text-sm font-bold text-red-800 mb-1">分析に失敗しました</p>
          <p className="text-xs text-red-700 mb-5">{errorMsg}</p>
          <button
            onClick={resetToInput}
            className="inline-flex items-center gap-2 bg-white text-red-700 border border-red-300 text-sm font-bold px-5 py-2.5 rounded-lg hover:bg-red-50 transition"
          >
            <RefreshCw className="h-4 w-4" />
            最初からやり直す
          </button>
        </div>
      )}

      {/* 印刷用 CSS: no-print を隠して、印刷専用ビュー (print-only) を A4 1枚にまとめる */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { size: A4; margin: 12mm; }
        }
        .print-only { display: none; }
        .print-page { font-feature-settings: "palt"; }
      `}</style>

      {/* ---------- Stage: result ---------- */}
      {stage === 'result' && result && (
        <div className="space-y-6 no-print">
          {/* Problem display — 全体の明るいデザインに合わせ、白カード + オレンジ左アクセント */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm border-l-4 border-l-safety-500">
            <p className="text-[11px] font-bold uppercase tracking-widest text-safety-600 mb-1.5">分析対象の事象</p>
            <p className="text-base md:text-lg font-bold leading-snug text-industrial-900">{result.problem}</p>
          </div>

          {/* 上部ツールバー: スクロールせず保存/印刷できるよう、結果の最上部に配置.
              (下部の Actions と重複するが、長い分析を全部スクロールしなくて済むよう発見性を優先) */}
          <div className="flex flex-wrap gap-2">
            {onSaveToCase && (
              <button
                onClick={() => {
                  trackEvent('why_demo_save_to_case', { from: 'why_demo_result_top' });
                  onSaveToCase(buildLessonText());
                }}
                className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold py-2.5 px-3 rounded-xl shadow-md hover:bg-emerald-700 transition text-sm"
              >
                <Save className="h-4 w-4" />
                ケースに保存
              </button>
            )}
            <button
              onClick={() => {
                trackEvent('why_demo_print_pdf', { from: 'why_demo_result_top' });
                window.print();
              }}
              className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 bg-sky-600 text-white font-bold py-2.5 px-3 rounded-xl shadow-md hover:bg-sky-700 transition text-sm"
            >
              <Printer className="h-4 w-4" />
              PDF / 印刷
            </button>
          </div>

          {/* 4M tabs — 「切り替えられる」と分かるよう、各観点を独立したカードボタンに.
              非選択も border+bg でボタンに見せ、選択中はオレンジ枠+塗りで明示. */}
          <div>
            <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
              👆 観点をタップで切り替え（4つの視点で分析しています）
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {FOUR_M_KEYS.map((key) => {
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    onClick={() => setActive(key)}
                    aria-pressed={isActive}
                    className={`px-3 md:px-4 py-3 rounded-xl text-sm md:text-base font-bold border-2 transition ${
                      isActive
                        ? 'bg-safety-500 text-white border-safety-500 shadow-md shadow-safety-500/30'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-safety-300 hover:bg-safety-50'
                    }`}
                  >
                    {fourMLabel(key)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected analysis */}
          <WhyTree analysis={result.analyses[active]} viewKey={active} />
          <Countermeasures analysis={result.analyses[active]} />

          {/* Disclaimer */}
          <Disclaimer />

          {/* Actions: 下部にも保存/印刷を再掲 (全部読んでから操作する人向け) + リセット */}
          <div className="flex flex-col sm:flex-row gap-2">
            {onSaveToCase && (
              <button
                onClick={() => {
                  trackEvent('why_demo_save_to_case', { from: 'why_demo_result_bottom' });
                  onSaveToCase(buildLessonText());
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 text-white border border-emerald-700 font-bold py-3 rounded-xl shadow-md hover:bg-emerald-700 transition"
              >
                <Save className="h-4 w-4" />
                ケースに保存
              </button>
            )}
            <button
              onClick={() => {
                trackEvent('why_demo_print_pdf', { from: 'why_demo_result_bottom' });
                window.print();
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-sky-600 text-white font-bold py-3 rounded-xl shadow-md hover:bg-sky-700 transition"
            >
              <Printer className="h-4 w-4" />
              PDFとして保存 (A4)
            </button>
            <button
              onClick={resetToInput}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-slate-700 border border-slate-300 font-bold py-3 rounded-xl hover:bg-slate-50 transition"
            >
              <RefreshCw className="h-4 w-4" />
              別の事象を分析する
            </button>
          </div>

          {/* 保存についての明示 (デモは保存されない) */}
          {!onSaveToCase && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed">
              <p className="font-bold text-slate-700 mb-1">📌 この結果は保存されません</p>
              <p>
                この画面はデモのため、ページを離れると結果は消えます。<br />
                結果を保存して後から見返したい場合は、安全ポスト＋に無料で登録してください (下記)。
              </p>
            </div>
          )}

          {/* 安全ポスト＋本体への送客 (集客装置の本命). WhyTrace+ より前に表示. */}
          {onSignupToAnzenPost && onShowAnzenPostDeck && (
            <AnzenPostCta onSignup={onSignupToAnzenPost} onDownloadDeck={onShowAnzenPostDeck} />
          )}

          {/* WhyTrace+ CTA (深掘り管理は WhyTrace+ で) */}
          <WhyTraceCta />
        </div>
      )}

      {/* 印刷専用ビュー: 4観点を縦に並べ、A4 1〜2枚に収まる体裁. 通常画面では非表示. */}
      {stage === 'result' && result && (
        <div className="print-only print-page text-black">
          <div className="border-b-2 border-slate-300 pb-3 mb-4">
            <div className="flex items-baseline justify-between">
              <h1 className="text-xl font-bold">4M なぜなぜ分析 結果</h1>
              <p className="text-xs text-slate-600">
                出力日: {new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
              </p>
            </div>
            <p className="text-sm mt-2"><strong>分析対象の事象:</strong> {result.problem}</p>
          </div>

          {FOUR_M_KEYS.map((k) => {
            const a = result.analyses[k];
            return (
              <section key={k} className="mb-4 break-inside-avoid">
                <h2 className="text-base font-bold border-l-4 border-amber-500 pl-2 mb-2">
                  【{fourMLabel(k)} の観点】
                </h2>
                <ol className="list-decimal ml-5 text-xs space-y-1.5">
                  {a.whys.map((w, i) => (
                    <li key={i}>
                      <span className="font-bold">{w.question}</span>
                      <br />
                      <span>{w.answer}</span>
                    </li>
                  ))}
                </ol>
                <p className="text-xs mt-2 bg-emerald-100 border-l-4 border-emerald-500 px-2 py-1.5">
                  <strong>推定される根本原因:</strong> {a.rootCause}
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="text-xs bg-amber-50 border border-amber-200 p-2">
                    <p className="font-bold text-amber-800 mb-1">即時対策: {a.immediate.title}</p>
                    <p>{a.immediate.description}</p>
                  </div>
                  <div className="text-xs bg-emerald-50 border border-emerald-200 p-2">
                    <p className="font-bold text-emerald-800 mb-1">恒久対策: {a.permanent.title}</p>
                    <p>{a.permanent.description}</p>
                  </div>
                </div>
              </section>
            );
          })}

          <p className="text-[10px] text-slate-500 border-t border-slate-300 pt-2 mt-4">
            ※ AI による簡易分析の補助です。実際の原因究明・対策決定は、現場の事実確認のうえで行ってください。
            <br />
            Powered by 安全ポスト＋ (anzenpost.com) — 4M なぜなぜ分析 デモ
          </p>
        </div>
      )}
    </div>
  );
};
